import { Observation } from "@medplum/fhirtypes";

/**
 * Picks the best-matching Observation out of a pregnancy record's candidates, preferring
 * `status: "final"`, then `status: "cancelled"`, then (only when `allowAnyStatus` is set, as
 * `fetchEDDFromPregnancyListEntries` does for EDD wrappers) any other non-error status with a
 * value — mirroring the fallback order of `fetchEDDForPregnancyRecord` /
 * `fetchLastMenstrualPeriodForPregnancyRecord`.
 *
 * Candidates are ordered newest-`meta.lastUpdated`-first, the same ordering
 * `_sort: "-_lastUpdated"` gives those live per-list searches.
 */
export function selectObservationByStatusPrecedence(
  observations: Observation[],
  { allowAnyStatus = false }: { allowAnyStatus?: boolean } = {},
): Observation | undefined {
  const candidates = observations
    .filter((observation) => observation.status !== "entered-in-error")
    .sort(
      (a, b) =>
        new Date(b.meta?.lastUpdated ?? 0).getTime() - new Date(a.meta?.lastUpdated ?? 0).getTime(),
    );

  const finalMatch = candidates.find((observation) => observation.status === "final");
  if (finalMatch) return finalMatch;

  const cancelledMatch = candidates.find((observation) => observation.status === "cancelled");
  if (cancelledMatch) return cancelledMatch;

  return allowAnyStatus ? candidates.find((observation) => observation.valueDateTime) : undefined;
}
